import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { filter, from, Observable, takeUntil } from 'rxjs';
import { ProductionLineHandlerData } from 'src/app/models_new/classes/3dview/handler-services/production-line-handler-data.service';
import { ApiProductionLine } from 'src/app/models_new/classes/api-models/ApiProduction_line';
import { Field } from 'src/app/models_new/classes/field';
import { FieldType } from 'src/app/models_new/types/field-type';
import { RXComponent } from '../../rx.component';
import capitalize from 'lodash-es/capitalize';
import { ThreeHandlerMode } from 'src/app/models_new/classes/3dview/three-handler-mode';
import { MatDialog } from '@angular/material/dialog';
import { PickerComponent } from '../../dialogs/picker/picker.component';
import { PickerType } from 'src/app/models_new/enums/picker-type';
import { clamp } from 'three/src/math/MathUtils';
import { NotificationService } from 'src/app/services/notification.service';

type OperationMode = 'option' | 'create' | 'pick' | 'edit' | 'view' | null;

enum PerDuration {
  Day = 'Day',
  Week = 'Week',
  Month = 'Month',
}
@Component({
  selector: 'app-production-line-card',
  templateUrl: './production-line-card.component.html',
  styleUrls: ['./production-line-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductionLineHandlerData],
})
export class ProductionLineCardComponent
  extends RXComponent
  implements OnChanges, OnInit
{
  // Wether it's used in an public setting
  @Input() isPublic = false;

  @Input()
  productionLine: ApiProductionLine | null;

  /** Allows to set an operation mode for the component to  */
  @Input()
  mode: OperationMode;

  /** Allows to browse between already selected Productrion Lines while in 'Pick' mode */
  @Input() selectedProductionLines: ApiProductionLine[] | null;

  @Input() hasProductionLines: boolean;

  /** Allows you to override the label on the create button */
  @Input()
  createLabel = 'CREATE';

  /** Event emitted when user wants to save the changes */
  @Output()
  readonly save = new EventEmitter<ApiProductionLine>();

  /** Event emitted when user request and edit */
  @Output()
  readonly edit = new EventEmitter<ApiProductionLine>();

  /** Event emitted when user pick from inventory */
  @Output()
  readonly add = new EventEmitter<ApiProductionLine[]>();

  /** Event emitted when user request cancelling the ongoing operation */
  @Output()
  readonly cancel = new EventEmitter<ApiProductionLine>();

  // Internal state
  nameField: Field;
  hoursField: Field;
  minutesField: Field;
  perField: Field;
  fields: Field[];

  cpm$: Observable<number>;

  // Init Operation Mode
  defaultMode: OperationMode;

  ThreeHandlerMode = ThreeHandlerMode;

  constructor(
    private dialog: MatDialog,
    private notification: NotificationService
  ) {
    super();

    // Setup fields for our card
    this.nameField = new Field(
      FieldType.TEXT,
      true,
      '', // Empty value until we get real value
      [Validators.required],
      null,
      null,
      null,
      { label: 'Name', name: 'name' },
      'name'
    );

    this.hoursField = new Field(
      FieldType.NUMBER,
      true,
      0, // Empty value until we get real value
      [Validators.required],
      null,
      null,
      null,
      { label: 'Hours', name: 'hours' },
      'hours',
      null,
      null,
      null,
      null,
      { min: 0, max: 24 }
    );

    this.minutesField = new Field(
      FieldType.NUMBER,
      true,
      0, // Empty value until we get real value
      [Validators.required],
      null,
      null,
      null,
      { label: 'Minutes', name: 'minutes' },
      'minutes',
      null,
      null,
      null,
      null,
      { min: 0, max: 59 }
    );

    this.perField = new Field(
      FieldType.SELECT_SINGLE,
      true,
      'Day',
      [Validators.required],
      null,
      Object.values(PerDuration),
      null,
      { label: 'Per', name: 'per' },
      'per'
    );

    // Store in array to easily disable / enable all of them
    this.fields = [
      this.nameField,
      this.hoursField,
      this.minutesField,
      this.perField,
    ];

    this.fields.forEach((field) => field.disable());

    this.cpm$ = from([0]);

    ////////////////////////////////////
    // Setup publishing to the 3D view
    ////////////////////////////////////

    // Listen to change of per field
    // to change the max-threshold for the hour field.
    this.perField.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue: PerDuration) => {
        switch (newValue) {
          case PerDuration.Day:
            // 24 hours in a day
            this.hoursField.max = 24;
            break;
          case PerDuration.Week:
            // 24 hours in a day and 7 days in a week
            this.hoursField.max = 24 * 7;
            break;
          case PerDuration.Month:
            // 24 hours in a day and max 30 days in a month
            this.hoursField.max = 24 * 30;
            break;
        }
        this.hoursField.validators[2] = Validators.max(this.hoursField.max);
        this.hoursField.formControl.setValidators(this.hoursField.validators);
        this.hoursField.formControl.updateValueAndValidity();
      });

    // Listen to change of hour field
    // to deactivate minute field if not plausible
    this.hoursField.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((value) => typeof value === 'string')
      )
      .subscribe((newValue: string) => {
        if (parseFloat(newValue) === this.hoursField.max) {
          this.minutesField.setValue(0);
          this.minutesField.formControl.disable();
        } else {
          this.minutesField.formControl.enable();
        }
      });
  }

  clampValueForField(field: Field, value: number): number {
    let min = field.min;
    if (min === undefined) {
      min = -Infinity;
    }

    let max = field.max;
    if (max === undefined) {
      max = Infinity;
    }

    return clamp(value, min, max);
  }

  ngOnInit() {
    this.defaultMode = this.mode;
  }

  ngOnChanges() {
    // We need both mode and production line before we do anything
    if (this.mode && this.productionLine) {
      this.refreshData();
    }
  }

  onSave() {
    // Extract values from the fields

    const isValid = this.validateFields();
    if (!isValid) {
      return;
    }

    const copy = this.productionLine.copy();
    copy.name = this.nameField.formControl.value;
    copy.data.hop = parseFloat(this.hoursField.formControl.value) || 0;
    copy.data.mop = parseFloat(this.minutesField.formControl.value) || 0;
    copy.data.per = this.perField.formControl.value.toLowerCase();
    this.save.emit(copy);
    this.mode = this.defaultMode;
  }

  /// Checks if fields are valid and shows a notification if not
  validateFields() {
    const invalidFields = this.findInvalidFields();
    if (invalidFields.length !== 0) {
      let fieldLabels = '';
      for (let i = 0; i < invalidFields.length; i++) {
        const field = invalidFields[i];
        fieldLabels += field.text.label;
        if (i !== invalidFields.length - 1) {
          if (i === invalidFields.length - 2) {
            fieldLabels += ' and ';
          } else {
            fieldLabels += ', ';
          }
        }
      }
      const message =
        invalidFields.length == 1
          ? ' has an invalid value.'
          : ' have invalid values.';
      this.notification.showError(fieldLabels + message);
      return false;
    }

    return true;
  }

  findInvalidFields(): Field[] {
    let invalidFields = [];
    for (const field of this.fields) {
      const status = field.formControl.status;
      if (!(status === 'VALID' || status === 'DISABLED')) {
        invalidFields.push(field);
      }
    }
    return invalidFields;
  }

  onSetToProject(prodlines: ApiProductionLine[]): void {
    /** Once ProdLine picked from inventory, set mode back to option to add more. */
    this.productionLine = ApiProductionLine.createEmpty();
    this.refreshData();
    this.add.emit(prodlines);
    this.mode = this.defaultMode;
  }

  onAddToProject(prodline: ApiProductionLine) {
    this.selectedProductionLines.push(prodline);
  }

  onModeSwitch(mode): void {
    ///////////////////////
    // Switch display mode
    ///////////////////////

    this.mode = mode;
    if (mode == 'create') {
      // Create an empty object to edit. Unlock fields.
      this.productionLine = ApiProductionLine.createEmpty();
    }
    this.refreshData();
  }

  showPicker() {
    const data = {
      type: PickerType.PRODUCTION_LINE,
      selectionAmount: 'SELECT_MULTIPLE',
    };
    const picker = this.dialog.open(PickerComponent, {
      data: data,
      width: 'calc(100vw - 2em)',
      height: 'calc(100vh - 2em)',
    });
    picker.afterClosed().subscribe((prodlines) => {
      if (prodlines) {
        this.onSetToProject(prodlines);
      }
    });
  }

  private refreshData(): void {
    /////////////////
    // Update fields
    /////////////////

    // Assign default values if data is missing
    const hop = this.productionLine.data.hop || 0;
    const mop = this.productionLine.data.mop || 0;
    const per = capitalize(this.productionLine.data.per) || 'Day';

    this.nameField.setValue(this.productionLine.name);
    this.hoursField.setValue(hop);
    this.minutesField.setValue(mop);
    this.perField.setValue(per);

    ////////////////////////////////
    // Enable / disable fields
    ////////////////////////////////

    if (this.mode === 'view' || this.mode === 'pick') {
      this.fields.forEach((f: Field) => f.disable());
    } else {
      this.fields.forEach((f: Field) => f.enable());
    }
  }

  onProdLineSelected($event): void {
    /** Set up fields for the selected object. */
    this.productionLine = $event.newVal;
    this.refreshData();
  }
}
